import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { Briefcase, FileText, MessageSquare, Star, Plus } from "lucide-react";
import DashboardLayout from "@/components/layout/DashboardLayout";
import PushToggle from "@/components/notifications/PushToggle";
import DanyDraftList from "@/components/dany/DanyDraftList";
import api from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";

export default function ClientDashboard() {
  const { t } = useTranslation();
  const { user } = useAuth();
  const name = user?.profile?.full_name?.split(" ")[0] || "—";

  // Live KPI counts from the backend (open jobs, quotes received, active chats,
  // completed jobs) instead of the previous hardcoded zeros.
  const [stats, setStats] = useState(null);
  useEffect(() => {
    api.get("/users/me/stats").then((r) => setStats(r.data)).catch(() => {});
  }, []);

  const kpis = [
    { key: "open", label: t("dashboard.client.kpiOpen"), value: stats?.open ?? 0, icon: Briefcase },
    { key: "quotes", label: t("dashboard.client.kpiQuotes"), value: stats?.quotes ?? 0, icon: FileText },
    { key: "convos", label: t("dashboard.client.kpiConvos"), value: stats?.convos ?? 0, icon: MessageSquare },
    { key: "completed", label: t("dashboard.client.kpiCompleted"), value: stats?.completed ?? 0, icon: Star },
  ];
  const hasActivity = !!stats && (stats.open + stats.quotes + stats.convos + stats.completed) > 0;

  return (
    <DashboardLayout role="client">
      <div data-testid="client-dashboard-content">
        <div className="flex items-start justify-between flex-wrap gap-4 mb-8">
          <div>
            <h1 className="font-display text-3xl md:text-4xl font-bold text-worker">
              {t("dashboard.client.hi", { name })}
            </h1>
            <p className="mt-1 text-smoke">{t("dashboard.client.tagline")}</p>
          </div>
          <Link to="/client/jobs/new" className="fx-btn fx-btn--primary" data-testid="post-new-job">
            <Plus size={18} /> {t("dashboard.client.postNewJob")}
          </Link>
        </div>

        {/* N36: one component, shared with "My jobs" — see DanyDraftList. */}
        <DanyDraftList className="mb-8" />

        <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-10">
          {kpis.map((k) => {
            const Icon = k.icon;
            return (
              <div key={k.key} className="card-base p-4" data-testid={`kpi-${k.key}`}>
                <div className="flex items-center justify-between">
                  <Icon size={18} className="text-smoke" />
                </div>
                <p className="mt-3 font-mono text-3xl font-bold text-worker">{k.value}</p>
                <p className="mt-1 text-xs uppercase tracking-widest text-smoke font-display">{k.label}</p>
              </div>
            );
          })}
        </div>

        {/* On the dashboard, not buried in Settings (Jorge, 2026-08-20): a
            notification you are not receiving is noticed on the page you look
            at every day. */}
        <div className="mb-10">
          <PushToggle />
        </div>

        {hasActivity ? (
          <div className="card-base p-8 flex items-center justify-between flex-wrap gap-4" data-testid="client-active-card">
            <div>
              <h3 className="font-display text-xl font-bold text-worker">{t("dashboard.client.activeTitle")}</h3>
              <p className="mt-1 text-smoke">{t("dashboard.client.activeBody")}</p>
            </div>
            <Link to="/client/jobs" className="fx-btn fx-btn--secondary" data-testid="view-my-jobs">
              <Briefcase size={18} /> {t("dashboard.client.viewJobs")}
            </Link>
          </div>
        ) : (
          <div className="card-base p-10 text-center">
            <h3 className="font-display text-2xl font-bold text-worker">{t("dashboard.client.emptyTitle")}</h3>
            <p className="mt-2 text-smoke max-w-md mx-auto">{t("dashboard.client.emptyBody")}</p>
            <Link to="/client/jobs/new" className="fx-btn fx-btn--secondary mt-6" data-testid="empty-post-job">
              <Plus size={18} /> {t("dashboard.client.emptyCta")}
            </Link>
          </div>
        )}

        {/* T25: the client door into the FixyBee side, on this same account.
            `/signup/provider` sees the session and asks only for what a
            FixyBee needs and a client lacks. */}
        <div className="card-base p-8 mt-6 flex items-center justify-between flex-wrap gap-4" data-testid="client-offer-services-card">
          <div>
            <h3 className="font-display text-xl font-bold text-worker">{t("dashboard.client.offerTitle")}</h3>
            <p className="mt-1 text-smoke">{t("dashboard.client.offerBody")}</p>
          </div>
          <Link to="/signup/provider" className="fx-btn fx-btn--secondary" data-testid="client-offer-services">
            {t("dashboard.client.offerCta")}
          </Link>
        </div>
      </div>
    </DashboardLayout>
  );
}
